import { forwardRef } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes, ReactNode } from 'react';

// PR-UX-08 atom · label + control + error feedback (Doherty <100ms inline).
// PR-UX-04 4 states · default / focus / error / disabled handled via styles.

interface BaseProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
}

interface InputFieldProps extends BaseProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  as?: 'input';
}
interface TextareaFieldProps extends BaseProps, TextareaHTMLAttributes<HTMLTextAreaElement> {
  as: 'textarea';
}

type FieldProps = InputFieldProps | TextareaFieldProps;

const fieldClasses = (hasError: boolean, isTextarea: boolean) =>
  `w-full px-4 py-3 text-[15px] bg-canvas border rounded transition focus:outline-none focus:ring-2 ${
    isTextarea ? 'resize-y overflow-y-auto max-h-[55vh] leading-relaxed' : ''
  } ${
    hasError
      ? 'border-vermilion focus:border-vermilion focus:ring-vermilion/15'
      : 'border-hairline focus:border-ink focus:ring-azure/15'
  }`;

const Field = forwardRef<HTMLInputElement | HTMLTextAreaElement, FieldProps>(function Field(props, ref) {
  const { label, hint, error, required, className = '', id, ...rest } = props;
  const fieldId = id || `field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const hasError = Boolean(error);

  return (
    <div className={className}>
      <label htmlFor={fieldId} className="block font-mono text-[11px] tracking-meta uppercase text-ink-2 mb-1.5">
        {label}
        {required && <span aria-hidden className="text-vermilion ml-1">*</span>}
      </label>
      {props.as === 'textarea' ? (
        <textarea
          id={fieldId}
          ref={ref as React.Ref<HTMLTextAreaElement>}
          className={fieldClasses(hasError, true)}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          id={fieldId}
          ref={ref as React.Ref<HTMLInputElement>}
          className={fieldClasses(hasError, false)}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          {...(rest as InputHTMLAttributes<HTMLInputElement>)}
        />
      )}
      {hint && !error && (
        <p id={`${fieldId}-hint`} className="text-[13px] text-ink-3 mt-1.5">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${fieldId}-error`} className="text-[13px] text-vermilion-700 mt-1.5">
          {error}
        </p>
      )}
    </div>
  );
});

export default Field;
